import React, { memo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { areEqual } from 'Utils/equalityChecks';
import classes from './thumbnail.module.css';
import { ImagePlaceHolder } from 'Components/ImagePlaceHolder';
import { Icon } from 'Components/Icons';

type ThumbnailStatus = 'loading' | 'loaded' | 'error';

const MAX_RETRY_ATTEMPTS = 3;
const LOAD_TIMEOUT_MS = 15_000;

// Module-level counter tracking how many thumbnail <img> loads are currently in-flight.
// Used to diagnose whether simultaneous failures are caused by connection concurrency.
let activeThumbnailLoads = 0;

const buildPerformanceEntrySnapshot = (entry?: PerformanceEntry) => {
  if (!entry) {
    return null;
  }

  const resourceEntry = entry as PerformanceResourceTiming & { responseStatus?: number };

  return {
    entryType: entry.entryType,
    name: entry.name,
    startTime: entry.startTime,
    duration: entry.duration,
    initiatorType: resourceEntry.initiatorType,
    transferSize: resourceEntry.transferSize,
    encodedBodySize: resourceEntry.encodedBodySize,
    decodedBodySize: resourceEntry.decodedBodySize,
    responseStatus: resourceEntry.responseStatus,
    nextHopProtocol: resourceEntry.nextHopProtocol,
    renderBlockingStatus: (resourceEntry as PerformanceResourceTiming & { renderBlockingStatus?: string })
      .renderBlockingStatus,
  };
};

const getLatestPerformanceEntry = (url?: string) => {
  if (!url || typeof window === 'undefined' || !window.performance?.getEntriesByName) {
    return undefined;
  }

  const entries = window.performance.getEntriesByName(url);

  return entries.length ? entries[entries.length - 1] : undefined;
};

const buildRetryUrl = (url: string, retryAttempt: number) => {
  try {
    const retryUrl = new URL(url);
    retryUrl.searchParams.set('retry', `${Date.now()}-${retryAttempt}`);
    return retryUrl.toString();
  } catch (error) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}retry=${Date.now()}-${retryAttempt}`;
  }
};

interface Props {
  id?: number;
  galleryInstanceId?: string;
  thumbnailSurface?: string;
  locationId?: number;
  locationName?: string;
  roomId?: number;
  roomName?: string;
  albumId?: number;
  galleryTitle?: string;
  placeHolderClassName?: string;
  imgClassName?: string;
  notesCount?: number;
  isBookmarked?: boolean;
  isFlagged?: boolean;
  thumbnailSrcUrl?: string;
  altString?: string;
  isPhotoSelected?: boolean;
  onClick?: (e: any) => void;
}

const Thumbnail = ({
  id,
  galleryInstanceId,
  thumbnailSurface,
  locationId,
  locationName,
  roomId,
  roomName,
  albumId,
  galleryTitle,
  placeHolderClassName,
  imgClassName,
  notesCount,
  isBookmarked,
  isFlagged,
  thumbnailSrcUrl,
  altString = '',
  isPhotoSelected,
  onClick,
}: Props) => {
  const [status, setStatus] = useState<ThumbnailStatus>(thumbnailSrcUrl ? 'loading' : 'error');
  const [currentSrc, setCurrentSrc] = useState(thumbnailSrcUrl);
  const lastLoggedFailedSrc = useRef<string | undefined>(undefined);
  const retryAttemptRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const thumbnailInstanceIdRef = useRef(`thumbnail-${Math.random().toString(36).slice(2, 10)}`);
  const thumbnailRequestedAtRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const lastRetryScheduledAtRef = useRef<number | null>(null);
  const lifecycleEventsRef = useRef<Record<string, unknown>[]>([]);
  const latestStateRef = useRef({
    currentSrc: thumbnailSrcUrl,
    status,
    thumbnailSrcUrl,
  });
  const telemetryContextRef = useRef({
    id,
    roomId,
    roomName,
    locationId,
    locationName,
    albumId,
    galleryTitle,
    galleryInstanceId,
    thumbnailSurface,
    thumbnailSrcUrl,
    altString,
  });
  const previousThumbnailSrcRef = useRef<string | undefined>(thumbnailSrcUrl);
  const isCountingRef = useRef(false);
  const loadTimeoutRef = useRef<number | null>(null);
  const retryImageLoadRef = useRef<((reason: 'error' | 'manual' | 'timeout') => boolean) | null>(null);

  latestStateRef.current = {
    currentSrc,
    status,
    thumbnailSrcUrl,
  };

  telemetryContextRef.current = {
    id,
    roomId,
    roomName,
    locationId,
    locationName,
    albumId,
    galleryTitle,
    galleryInstanceId,
    thumbnailSurface,
    thumbnailSrcUrl,
    altString,
  };

  const recordLifecycleEvent = useCallback((type: string, data: Record<string, unknown> = {}, level: Sentry.SeverityLevel = 'info') => {
    const atMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const latestState = latestStateRef.current;
    const telemetryContext = telemetryContextRef.current;
    const nextEvent = {
      type,
      atMs,
      ...data,
    };

    lifecycleEventsRef.current = [...lifecycleEventsRef.current.slice(-9), nextEvent];

    Sentry.addBreadcrumb({
      category: 'thumbnail',
      level,
      message: `thumbnail.${type}`,
      data: {
        thumbnailInstanceId: thumbnailInstanceIdRef.current,
        status: latestState.status,
        currentSrc: latestState.currentSrc,
        ...telemetryContext,
        ...data,
      },
    });
  }, []);

  useEffect(() => {
    if (
      previousThumbnailSrcRef.current &&
      previousThumbnailSrcRef.current !== thumbnailSrcUrl &&
      latestStateRef.current.status === 'loading'
    ) {
      recordLifecycleEvent(
        'src_changed_while_loading',
        {
          previousThumbnailSrcUrl: previousThumbnailSrcRef.current,
          nextThumbnailSrcUrl: thumbnailSrcUrl,
          retryAttempt: retryAttemptRef.current,
        },
        'warning'
      );
    }

    if (isCountingRef.current) {
      activeThumbnailLoads -= 1;
      isCountingRef.current = false;
    }

    setStatus(thumbnailSrcUrl ? 'loading' : 'error');
    setCurrentSrc(thumbnailSrcUrl);
    lastLoggedFailedSrc.current = undefined;
    retryAttemptRef.current = 0;
    thumbnailRequestedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    lastRetryScheduledAtRef.current = null;
    previousThumbnailSrcRef.current = thumbnailSrcUrl;
    lifecycleEventsRef.current = [];

    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (loadTimeoutRef.current) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, [thumbnailSrcUrl]);

  // Track concurrent in-flight loads. useLayoutEffect runs synchronously after the DOM commits
  // (img.src is set), before async image events fire, so onLoad/onError can never race ahead
  // of this increment — even for cached images that load very quickly.
  useLayoutEffect(() => {
    if (currentSrc) {
      activeThumbnailLoads += 1;
      isCountingRef.current = true;
    }
  }, [currentSrc]);

  useEffect(
    () => () => {
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }

      if (loadTimeoutRef.current) {
        window.clearTimeout(loadTimeoutRef.current);
      }

      if (isCountingRef.current) {
        activeThumbnailLoads -= 1;
        isCountingRef.current = false;
      }

      const latestState = latestStateRef.current;
      if (latestState.status === 'loading' && latestState.currentSrc) {
        recordLifecycleEvent(
          'unmount_while_loading',
          {
            retryAttempt: retryAttemptRef.current,
            currentSrc: latestState.currentSrc,
          },
          'warning'
        );
      }
    },
    []
  );

  const onLoad = useCallback(() => {
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (loadTimeoutRef.current) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    if (isCountingRef.current) {
      activeThumbnailLoads -= 1;
      isCountingRef.current = false;
    }

    setStatus('loaded');
    if (retryAttemptRef.current > 0) {
      recordLifecycleEvent('loaded_after_retry', {
        retryAttempt: retryAttemptRef.current,
        currentSrc,
      });
    }
  }, [currentSrc, recordLifecycleEvent]);

  const retryImageLoad = useCallback(
    (reason: 'error' | 'manual' | 'timeout') => {
      if (!thumbnailSrcUrl || retryAttemptRef.current >= MAX_RETRY_ATTEMPTS) {
        return false;
      }

      retryAttemptRef.current += 1;
      const nextSrc = buildRetryUrl(thumbnailSrcUrl, retryAttemptRef.current);

      const retryMessage = reason === 'manual'
        ? 'Thumbnail retry requested manually'
        : reason === 'timeout'
        ? 'Thumbnail load timed out, retrying'
        : 'Thumbnail failed to load, retrying';

      Sentry.captureMessage(retryMessage, {
        level: reason === 'manual' ? 'info' : 'warning',
        tags: {
          feature: 'thumbnail',
          hasVersionParam: thumbnailSrcUrl.includes('?v=') ? 'yes' : 'no',
          retryReason: reason,
        },
        extra: {
          id,
          thumbnailSrcUrl,
          attemptedSrc: currentSrc,
          nextSrc,
          retryAttempt: retryAttemptRef.current,
          maxRetryAttempts: MAX_RETRY_ATTEMPTS,
          thumbnailSurface,
          galleryInstanceId,
          locationId,
          locationName,
          roomId,
          roomName,
          albumId,
          galleryTitle,
          pathname: window.location.pathname,
          search: window.location.search,
          online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        },
      });

      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }

      lastRetryScheduledAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
      recordLifecycleEvent('retry_scheduled', {
        reason,
        retryAttempt: retryAttemptRef.current,
        attemptedSrc: currentSrc,
        nextSrc,
      });
      retryTimeoutRef.current = window.setTimeout(() => {
        setCurrentSrc(nextSrc);
        setStatus('loading');
      }, reason === 'manual' ? 0 : retryAttemptRef.current * 250);

      return true;
    },
    [currentSrc, id, recordLifecycleEvent, thumbnailSrcUrl]
  );

  // Keep ref current so the load-timeout handler always calls the latest version
  // without capturing a stale closure of retryImageLoad.
  retryImageLoadRef.current = retryImageLoad;

  // Start a load timeout whenever currentSrc changes. If neither onLoad nor onError
  // fires within LOAD_TIMEOUT_MS the thumbnail is considered hung (e.g. a connection
  // that was opened but never completed). The handler follows the same retry/Sentry
  // path as onError so hung loads are visible in Sentry and eventually surface
  // "Image unavailable" + manual retry to the user instead of a permanent placeholder.
  useEffect(() => {
    if (!currentSrc) return;

    if (loadTimeoutRef.current) {
      window.clearTimeout(loadTimeoutRef.current);
    }

    loadTimeoutRef.current = window.setTimeout(() => {
      loadTimeoutRef.current = null;

      if (latestStateRef.current.status !== 'loading') return;

      const timedOutSrc = latestStateRef.current.currentSrc;
      const concurrentLoadsAtFailure = activeThumbnailLoads;

      if (isCountingRef.current) {
        activeThumbnailLoads -= 1;
        isCountingRef.current = false;
      }

      recordLifecycleEvent(
        'load_timeout',
        {
          currentSrc: timedOutSrc,
          retryAttempt: retryAttemptRef.current,
          concurrentLoadsAtFailure,
          timeoutMs: LOAD_TIMEOUT_MS,
        },
        'warning'
      );

      if (retryImageLoadRef.current?.('timeout')) {
        return;
      }

      // No retries remaining — same final path as onError.
      setStatus('error');

      const currentThumbnailSrcUrl = latestStateRef.current.thumbnailSrcUrl;
      if (!currentThumbnailSrcUrl || lastLoggedFailedSrc.current === currentThumbnailSrcUrl) return;

      lastLoggedFailedSrc.current = currentThumbnailSrcUrl;
      Sentry.captureException(new Error('Thumbnail load timed out'), {
        tags: {
          feature: 'thumbnail',
          hasVersionParam: currentThumbnailSrcUrl.includes('?v=') ? 'yes' : 'no',
          retried: retryAttemptRef.current > 0 ? 'yes' : 'no',
          failureReason: 'timeout',
        },
        extra: {
          ...telemetryContextRef.current,
          thumbnailInstanceId: thumbnailInstanceIdRef.current,
          timedOutSrc,
          retryAttempt: retryAttemptRef.current,
          maxRetryAttempts: MAX_RETRY_ATTEMPTS,
          concurrentLoadsAtFailure,
          timeoutMs: LOAD_TIMEOUT_MS,
          lifecycleEvents: lifecycleEventsRef.current,
          performanceEntry: buildPerformanceEntrySnapshot(getLatestPerformanceEntry(timedOutSrc || undefined)),
          pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
          online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        },
      });
    }, LOAD_TIMEOUT_MS);

    return () => {
      if (loadTimeoutRef.current) {
        window.clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, [currentSrc, recordLifecycleEvent]);

  const onAbort = useCallback(() => {
    // Do not decrement here. abort fires after the browser cancels an in-flight request
    // (e.g. src change, unmount), which always follows a path that has already decremented:
    // prop change → useEffect([thumbnailSrcUrl]) decrements before the new src is set;
    // unmount → cleanup effect decrements. Decrementing here races with those paths.
    const imageElement = imageRef.current;
    const abortPerformanceEntry = getLatestPerformanceEntry(currentSrc || imageElement?.currentSrc);

    recordLifecycleEvent(
      'abort',
      {
        attemptedSrc: currentSrc,
        imgCurrentSrc: imageElement?.currentSrc || '',
        imgComplete: imageElement?.complete ?? false,
        naturalWidth: imageElement?.naturalWidth ?? 0,
        naturalHeight: imageElement?.naturalHeight ?? 0,
        retryAttempt: retryAttemptRef.current,
        performanceEntry: buildPerformanceEntrySnapshot(abortPerformanceEntry),
      },
      'warning'
    );
  }, [currentSrc, recordLifecycleEvent]);

  const onError = useCallback(() => {
    if (loadTimeoutRef.current) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    if (!currentSrc) {
      setStatus('error');
      return;
    }

    const imageElement = imageRef.current;
    const pathname = window.location.pathname;
    const search = window.location.search;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const currentPerformanceEntry = getLatestPerformanceEntry(currentSrc);
    const originalPerformanceEntry = currentSrc !== thumbnailSrcUrl ? getLatestPerformanceEntry(thumbnailSrcUrl) : undefined;
    const imagePerformanceEntry = imageElement?.currentSrc ? getLatestPerformanceEntry(imageElement.currentSrc) : undefined;
    const recentResourceEntries = typeof window !== 'undefined' && window.performance?.getEntriesByType
      ? (window.performance.getEntriesByType('resource') as PerformanceResourceTiming[])
          .filter((entry) => entry.name.includes('storage.rocketplantech.com'))
          .slice(-5)
          .map((entry) => buildPerformanceEntrySnapshot(entry))
      : [];
    const navigatorConnection = typeof navigator !== 'undefined'
      ? (
          navigator as Navigator & {
            connection?: {
              effectiveType?: string;
              downlink?: number;
              rtt?: number;
              saveData?: boolean;
            };
          }
        ).connection
      : undefined;

    // Snapshot before decrementing so the count includes this instance.
    const concurrentLoadsAtFailure = activeThumbnailLoads;

    // Decrement on every error — re-increment happens via useEffect([currentSrc]) when the
    // retry URL is set, so the count accurately reflects zero during backoff windows.
    if (isCountingRef.current) {
      activeThumbnailLoads -= 1;
      isCountingRef.current = false;
    }

    const failureContext = {
      id,
      thumbnailSrcUrl,
      attemptedSrc: currentSrc,
      altString,
      currentSrc: imageElement?.currentSrc || '',
      imgComplete: imageElement?.complete ?? false,
      naturalWidth: imageElement?.naturalWidth ?? 0,
      naturalHeight: imageElement?.naturalHeight ?? 0,
      pathname,
      search,
      referrer: document.referrer || '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : undefined,
      readyState: typeof document !== 'undefined' ? document.readyState : undefined,
      dpr: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
      requestedAtMs: thumbnailRequestedAtRef.current,
      failedAtMs: now,
      elapsedSinceRequestMs: now - thumbnailRequestedAtRef.current,
      lastRetryScheduledAtMs: lastRetryScheduledAtRef.current,
      elapsedSinceLastRetryMs: lastRetryScheduledAtRef.current ? now - lastRetryScheduledAtRef.current : null,
      thumbnailInstanceId: thumbnailInstanceIdRef.current,
      thumbnailSurface,
      galleryInstanceId,
      locationId,
      locationName,
      roomId,
      roomName,
      albumId,
      galleryTitle,
      connection: navigatorConnection
        ? {
            effectiveType: navigatorConnection.effectiveType,
            downlink: navigatorConnection.downlink,
            rtt: navigatorConnection.rtt,
            saveData: navigatorConnection.saveData,
          }
        : null,
      performanceEntry: buildPerformanceEntrySnapshot(currentPerformanceEntry),
      originalPerformanceEntry: buildPerformanceEntrySnapshot(originalPerformanceEntry),
      imageCurrentSrcPerformanceEntry: buildPerformanceEntrySnapshot(imagePerformanceEntry),
      recentResourceEntries,
      lifecycleEvents: lifecycleEventsRef.current,
      retryAttempt: retryAttemptRef.current,
      maxRetryAttempts: MAX_RETRY_ATTEMPTS,
      concurrentLoadsAtFailure,
    };

    if (retryImageLoad('error')) {
      return;
    }

    setStatus('error');
    recordLifecycleEvent('final_error', {
      retryAttempt: retryAttemptRef.current,
      attemptedSrc: currentSrc,
      concurrentLoadsAtFailure,
      currentPerformanceEntry: buildPerformanceEntrySnapshot(currentPerformanceEntry),
      originalPerformanceEntry: buildPerformanceEntrySnapshot(originalPerformanceEntry),
    }, 'error');

    if (!thumbnailSrcUrl || lastLoggedFailedSrc.current === thumbnailSrcUrl) return;

    lastLoggedFailedSrc.current = thumbnailSrcUrl;
    Sentry.captureException(new Error('Thumbnail failed to load'), {
      tags: {
        feature: 'thumbnail',
        hasVersionParam: thumbnailSrcUrl.includes('?v=') ? 'yes' : 'no',
        retried: retryAttemptRef.current > 0 ? 'yes' : 'no',
      },
      extra: {
        ...failureContext,
      },
    });
  }, [albumId, altString, currentSrc, galleryInstanceId, galleryTitle, id, locationId, locationName, recordLifecycleEvent, retryImageLoad, roomId, roomName, thumbnailSrcUrl, thumbnailSurface]);

  return (
    <div className={classes.container}>
      {status === 'loading' && <ImagePlaceHolder placeHolderClassName={placeHolderClassName} />}
      <img
        id={id && id.toString()}
        ref={imageRef}
        onLoad={onLoad}
        onAbort={onAbort}
        onError={onError}
        onClick={onClick}
        src={currentSrc}
        alt={altString || 'image'}
        className={`${classes.imgReSizing} ${classes.dimensions} ${classes.pointer}
      ${status === 'loaded' ? classes.visible : classes.hidden}
      ${imgClassName ? imgClassName : ''}
       ${isPhotoSelected ? classes.isPhotoSelected : ''}
      `}
      />
      {status === 'error' && (
        <div
          className={`d-flex flex-column align-items-center justify-content-center ${classes.dimensions} ${classes.pointer}
          ${classes.failedThumbnail} ${imgClassName ? imgClassName : ''}
          ${isPhotoSelected ? classes.isPhotoSelected : ''}`}
          onClick={(event) => {
            const handledRetry = retryImageLoad('manual');

            if (!handledRetry && onClick) {
              onClick(event);
            }
          }}
        >
          <Icon className={classes.failedThumbnailIcon} type="galleryplaceholder" />
          <div className={classes.failedThumbnailText}>Image unavailable</div>
        </div>
      )}
      {notesCount > 0 && <Icon className={classes.photoNote} type="photonote" />}
      <div className={classes.topIcons}>
        {isFlagged && <Icon className={classes.photoFlag} type="photoflag" />}
        {isBookmarked && <Icon className={classes.photoBookmark} type="photobookmark" />}
      </div>
      <div className={classes.bottomIcons}>
        {isPhotoSelected && <Icon className={classes.photoBookmark} type="confirmpurple" />}
      </div>
    </div>
  );
};
Thumbnail.defaultProps = {
  placeHolderClassName: undefined,
  imgClassName: undefined,
  onClick: undefined,
  altString: undefined,
  thumbnailSrcUrl: undefined,
  isPhotoSelected: false,
};
const ThumbnailMemo = memo(Thumbnail, areEqual);
export { ThumbnailMemo as Thumbnail };
